import { supabase } from '@/lib/supabase'

const FEATURE_FLAGS_UPDATED_EVENT = 'drvn:feature-flags-updated'
const FLAG_CACHE_TTL_MS = 60 * 1000

const DEFAULT_FLAGS = {
  drive_radar_enabled: true,
  messages_enabled: true,
  stickers_enabled: true,
  tournament_enabled: true,
}

let cachedFlags = { ...DEFAULT_FLAGS }
let lastLoadedAt = 0

const normalizeFlags = (rows = []) => {
  const nextFlags = { ...DEFAULT_FLAGS }
  rows.forEach((row) => {
    if (!row?.key) return
    nextFlags[row.key] = row.enabled !== false
  })
  return nextFlags
}

export const getCachedFeatureFlags = () => ({ ...cachedFlags })

export const isFeatureEnabled = (key) => {
  if (!key) return true
  return cachedFlags[key] !== false
}

export const syncFeatureFlagCache = (rows = []) => {
  cachedFlags = normalizeFlags(rows)
  lastLoadedAt = Date.now()
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(FEATURE_FLAGS_UPDATED_EVENT, { detail: getCachedFeatureFlags() }))
  }
  return getCachedFeatureFlags()
}

export const loadFeatureFlags = async (options = {}) => {
  const { force = false } = options
  if (!force && Date.now() - lastLoadedAt < FLAG_CACHE_TTL_MS) {
    return getCachedFeatureFlags()
  }

  const { data, error } = await supabase
    .from('app_feature_flags')
    .select('key, enabled')

  if (error) throw error
  return syncFeatureFlagCache(data || [])
}

export const updateFeatureFlag = async (key, enabled, details = {}) => {
  const payload = {
    key,
    label: details.label || key,
    description: details.description || '',
    enabled: enabled !== false,
    updated_at: new Date().toISOString(),
    updated_by: details.updatedBy || null,
  }

  const { error } = await supabase
    .from('app_feature_flags')
    .upsert(payload, { onConflict: 'key' })

  if (error) throw error

  cachedFlags = {
    ...cachedFlags,
    [key]: payload.enabled,
  }
  lastLoadedAt = Date.now()
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(FEATURE_FLAGS_UPDATED_EVENT, { detail: getCachedFeatureFlags() }))
  }
  return getCachedFeatureFlags()
}

export const onFeatureFlagsUpdated = (handler) => {
  if (typeof window === 'undefined' || typeof handler !== 'function') return () => {}
  const listener = (event) => {
    handler(event?.detail || getCachedFeatureFlags())
  }
  window.addEventListener(FEATURE_FLAGS_UPDATED_EVENT, listener)
  return () => window.removeEventListener(FEATURE_FLAGS_UPDATED_EVENT, listener)
}

export { FEATURE_FLAGS_UPDATED_EVENT }
